import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllEmps } from "../../redux/empSlice";
import { getAllEmpWorks } from "../../redux/empworkSlice";
import { getAllParties } from "../../redux/billSlice";
import { getAllProcesses } from "../../redux/billSlice";
import { getAllBills } from "../../redux/billSlice";
import { getAllPayments } from "../../redux/paymentSlice";
import { Helmet } from "react-helmet-async";

const Home = () => {
  const { user } = useSelector((state) => state.user);
  const { emps } = useSelector((state) => state.emp);
  const { empworks } = useSelector((state) => state.empwork);
  const { parties, processes, bills } = useSelector((state) => state.bill);
  const { payments } = useSelector((state) => state.payment);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Load data for statistics
  useEffect(() => {
    dispatch(getAllEmps());
    dispatch(getAllEmpWorks());
    dispatch(getAllParties());
    dispatch(getAllProcesses());
    dispatch(getAllBills());
    dispatch(getAllPayments());
  }, [dispatch]);

  // Master Data Links
  const masterDataLinks = [
    { path: "/process", label: "Process", icon: "⚙️", color: "#667eea", description: "Manage processes" },
    { path: "/party", label: "Party", icon: "👥", color: "#764ba2", description: "Manage parties" },
    { path: "/emp", label: "Employee", icon: "👤", color: "#f093fb", description: "Manage employees" },
    { path: "/empwork", label: "Emp Work", icon: "💼", color: "#4facfe", description: "Manage work types" },
  ];

  // Transaction Links
  const transactionLinks = [
    { path: "/daily-work-entry", label: "Daily Work Entry", icon: "📝", color: "#10b981", description: "Record daily work" },
    { path: "/payment", label: "Payment", icon: "💳", color: "#43e97b", description: "Manage payments" },
    { path: "/bill", label: "Bill", icon: "📄", color: "#fa709a", description: "Manage bills" },
  ];

  // Report Links
  const reportLinks = [
    { path: "/work-report", label: "Work Report", icon: "📋", color: "#06b6d4", description: "View work reports" },
    { path: "/bill-report", label: "Bill Report", icon: "📊", color: "#fee140", description: "View bill reports" },
    { path: "/emp-report", label: "Emp Report", icon: "📈", color: "#30cfd0", description: "View employee reports" },
    { path: "/outstanding-payments", label: "Outstanding Payments", icon: "💰", color: "#f59e0b", description: "View outstanding payments" },
  ];

  const allLinks = [...masterDataLinks, ...transactionLinks, ...reportLinks];

  return (
    <>
      <Helmet>
        <title>Home - RTS Books</title>
      </Helmet>
      <style>{`
        .home-container {
          min-height: calc(100vh - 70px);
          background: linear-gradient(135deg, #f0f4ff 0%, #e0e7ff 50%, #c7d2fe 100%);
          padding: 40px 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .home-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        .home-header {
          text-align: center;
          margin-bottom: 50px;
          color: #1e40af;
        }

        .home-title {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #1e40af;
          letter-spacing: -0.5px;
        }

        .home-subtitle {
          font-size: 1.3rem;
          color: #1e3a8a;
          margin-bottom: 10px;
        }

        .home-welcome {
          font-size: 1.1rem;
          color: #1e293b;
        }

        .home-user-name {
          font-weight: 700;
          text-decoration: underline;
        }

        .home-section {
          margin-bottom: 50px;
        }

        .home-section-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 25px;
          text-align: center;
          letter-spacing: 0.3px;
        }

        .home-cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 25px;
          margin-bottom: 40px;
        }

        .home-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          position: relative;
          overflow: hidden;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
          user-select: none;
        }

        .home-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--card-color);
        }

        .home-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 30px rgba(30, 64, 175, 0.15);
          border-color: #3b82f6;
        }

        .home-card-icon {
          font-size: 4rem;
          margin-bottom: 5px;
        }

        .home-card-label {
          font-size: 1.3rem;
          font-weight: 700;
          color: #1e293b;
          text-align: center;
        }

        .home-card-description {
          font-size: 0.9rem;
          color: #475569;
          text-align: center;
          margin-top: -5px;
        }

        .home-stats {
          background: #ffffff;
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 4px 20px rgba(30, 64, 175, 0.08);
          border: 1px solid #e0e7ff;
          margin-top: 30px;
        }

        .home-stats-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 20px;
          text-align: center;
          letter-spacing: 0.3px;
        }

        .home-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
        }

        .home-stat-item {
          text-align: center;
          padding: 25px 20px;
          background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
          border-radius: 12px;
          color: white;
          transition: transform 0.3s ease;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }

        .home-stat-item:hover {
          transform: scale(1.05);
        }

        .home-stat-label {
          font-size: 0.9rem;
          opacity: 0.9;
          margin-bottom: 8px;
        }

        .home-stat-value {
          font-size: 2.5rem;
          font-weight: 700;
        }

        /* Large Tablets */
        @media (max-width: 1024px) {
          .home-container {
            padding: 35px 20px;
          }

          .home-wrapper {
            max-width: 100%;
          }

          .home-title {
            font-size: 2.5rem;
          }

          .home-section-title {
            font-size: 1.8rem;
          }

          .home-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
          }

          .home-stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          }

          .home-stat-value {
            font-size: 2.2rem;
          }
        }

        /* Tablets */
        @media (max-width: 768px) {
          .home-container {
            padding: 25px 15px;
          }

          .home-header {
            margin-bottom: 35px;
          }

          .home-title {
            font-size: 2rem;
            margin-bottom: 10px;
          }

          .home-subtitle {
            font-size: 1.1rem;
            margin-bottom: 8px;
          }

          .home-welcome {
            font-size: 1rem;
          }

          .home-section {
            margin-bottom: 35px;
          }

          .home-section-title {
            font-size: 1.6rem;
            margin-bottom: 20px;
          }

          .home-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 18px;
            margin-bottom: 30px;
          }

          .home-card {
            padding: 22px;
            border-radius: 16px;
          }

          .home-card-icon {
            font-size: 3rem;
            margin-bottom: 5px;
          }

          .home-card-label {
            font-size: 1.1rem;
          }

          .home-card-description {
            font-size: 0.85rem;
          }

          .home-stats {
            padding: 25px;
            border-radius: 16px;
            margin-top: 25px;
          }

          .home-stats-title {
            font-size: 1.6rem;
            margin-bottom: 18px;
          }

          .home-stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
            gap: 15px;
          }

          .home-stat-item {
            padding: 20px 15px;
            border-radius: 12px;
          }

          .home-stat-label {
            font-size: 0.85rem;
          }

          .home-stat-value {
            font-size: 2rem;
          }
        }

        /* Small Tablets and Large Phones */
        @media (max-width: 640px) {
          .home-container {
            padding: 20px 12px;
          }

          .home-header {
            margin-bottom: 30px;
          }

          .home-title {
            font-size: 1.75rem;
          }

          .home-subtitle {
            font-size: 1rem;
          }

          .home-section-title {
            font-size: 1.4rem;
            margin-bottom: 18px;
          }

          .home-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            gap: 15px;
          }

          .home-card {
            padding: 20px 15px;
          }

          .home-card-icon {
            font-size: 2.5rem;
          }

          .home-card-label {
            font-size: 1rem;
          }

          .home-card-description {
            font-size: 0.8rem;
          }

          .home-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .home-stat-item {
            padding: 18px 12px;
          }

          .home-stat-value {
            font-size: 1.8rem;
          }
        }

        /* Mobile Phones */
        @media (max-width: 480px) {
          .home-container {
            padding: 15px 10px;
            min-height: calc(100vh - 60px);
          }

          .home-wrapper {
            padding: 0;
          }

          .home-header {
            margin-bottom: 25px;
          }

          .home-title {
            font-size: 1.5rem;
            margin-bottom: 8px;
            line-height: 1.2;
          }

          .home-subtitle {
            font-size: 0.95rem;
            margin-bottom: 6px;
          }

          .home-welcome {
            font-size: 0.9rem;
          }

          .home-user-name {
            display: block;
            margin-top: 4px;
          }

          .home-section {
            margin-bottom: 25px;
          }

          .home-section-title {
            font-size: 1.25rem;
            margin-bottom: 15px;
          }

          .home-cards-grid {
            grid-template-columns: 1fr;
            gap: 12px;
            margin-bottom: 20px;
          }

          .home-card {
            padding: 18px;
            border-radius: 14px;
            gap: 10px;
          }

          .home-card:hover {
            transform: translateY(-5px);
          }

          .home-card-icon {
            font-size: 2.5rem;
            margin-bottom: 5px;
          }

          .home-card-label {
            font-size: 1rem;
            font-weight: 600;
          }

          .home-card-description {
            font-size: 0.75rem;
            margin-top: -3px;
          }

          .home-stats {
            padding: 20px 15px;
            border-radius: 14px;
            margin-top: 20px;
          }

          .home-stats-title {
            font-size: 1.3rem;
            margin-bottom: 15px;
          }

          .home-stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .home-stat-item {
            padding: 15px 10px;
            border-radius: 10px;
          }

          .home-stat-item:hover {
            transform: scale(1.02);
          }

          .home-stat-label {
            font-size: 0.75rem;
            margin-bottom: 6px;
          }

          .home-stat-value {
            font-size: 1.5rem;
          }

          .home-stat-value[style*="font-size: 1.5rem"] {
            font-size: 1.2rem !important;
          }
        }

        /* Extra Small Phones */
        @media (max-width: 360px) {
          .home-container {
            padding: 12px 8px;
          }

          .home-title {
            font-size: 1.3rem;
          }

          .home-subtitle {
            font-size: 0.85rem;
          }

          .home-section-title {
            font-size: 1.1rem;
          }

          .home-card {
            padding: 15px;
          }

          .home-card-icon {
            font-size: 2rem;
          }

          .home-card-label {
            font-size: 0.95rem;
          }

          .home-card-description {
            font-size: 0.7rem;
          }

          .home-stats {
            padding: 15px 12px;
          }

          .home-stats-title {
            font-size: 1.1rem;
          }

          .home-stats-grid {
            gap: 8px;
          }

          .home-stat-item {
            padding: 12px 8px;
          }

          .home-stat-label {
            font-size: 0.7rem;
          }

          .home-stat-value {
            font-size: 1.3rem;
          }
        }

        /* Landscape Mobile */
        @media (max-width: 768px) and (orientation: landscape) {
          .home-container {
            padding: 20px 15px;
          }

          .home-header {
            margin-bottom: 20px;
          }

          .home-title {
            font-size: 1.8rem;
          }

          .home-section {
            margin-bottom: 25px;
          }

          .home-cards-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 12px;
          }

          .home-card {
            padding: 18px;
          }
        }
      `}</style>

      <div className="home-container">
        <div className="home-wrapper">
          <div className="home-header">
            <h1 className="home-title">Welcome to SHREE HAREKRISHNA GAAJ CENTER</h1>
            <p className="home-subtitle">Management System</p>
            {user && (
              <p className="home-welcome">
                Welcome back, <span className="home-user-name">{user.name}</span>!
              </p>
            )}
          </div>

          {/* Master Data Section */}
          <div className="home-section">
            <h2 className="home-section-title">📋 Master Data</h2>
            <div className="home-cards-grid">
              {masterDataLinks.map((link) => (
                <div
                  key={link.path}
                  className="home-card"
                  onClick={() => navigate(link.path)}
                  style={{ "--card-color": link.color }}
                >
                  <div className="home-card-icon">{link.icon}</div>
                  <div className="home-card-label">{link.label}</div>
                  <div className="home-card-description">{link.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Transaction Section */}
          <div className="home-section">
            <h2 className="home-section-title">💼 Transactions</h2>
            <div className="home-cards-grid">
              {transactionLinks.map((link) => (
                <div
                  key={link.path}
                  className="home-card"
                  onClick={() => navigate(link.path)}
                  style={{ "--card-color": link.color }}
                >
                  <div className="home-card-icon">{link.icon}</div>
                  <div className="home-card-label">{link.label}</div>
                  <div className="home-card-description">{link.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reports Section */}
          <div className="home-section">
            <h2 className="home-section-title">📊 Reports</h2>
            <div className="home-cards-grid">
              {reportLinks.map((link) => (
                <div
                  key={link.path}
                  className="home-card"
                  onClick={() => navigate(link.path)}
                  style={{ "--card-color": link.color }}
                >
                  <div className="home-card-icon">{link.icon}</div>
                  <div className="home-card-label">{link.label}</div>
                  <div className="home-card-description">{link.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Statistics Section */}
          <div className="home-stats">
            <h2 className="home-stats-title">📈 Statistics</h2>
            <div className="home-stats-grid">
              <div className="home-stat-item">
                <div className="home-stat-label">Total Employees</div>
                <div className="home-stat-value">{emps?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Work Types</div>
                <div className="home-stat-value">{empworks?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Parties</div>
                <div className="home-stat-value">{parties?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Processes</div>
                <div className="home-stat-value">{processes?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Total Bills</div>
                <div className="home-stat-value">{bills?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Total Payments</div>
                <div className="home-stat-value">{payments?.length || 0}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">Total Modules</div>
                <div className="home-stat-value">{allLinks.length}</div>
              </div>
              <div className="home-stat-item">
                <div className="home-stat-label">User Role</div>
                <div className="home-stat-value" style={{ fontSize: "1.5rem" }}>
                  {user?.role || "User"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
